import React, { useState, useEffect } from "react";
import { DashboardLayout } from "../../client/components/Sidebar";
import DefaultAdminLayout from "../../admin/layout/DefaultLayout";
import { type AuthUser } from "wasp/auth";

import { LucideIcon } from "lucide-react";

export interface IndividualEditPageLayoutProps {
  children: React.ReactNode;
  sidebarContent?: React.ReactNode;
  sidebar?: React.ReactNode; // Alias for sidebarContent
  actions?: React.ReactNode; // Dedicated actions prop for mobile bottom bar
  customTopBarContent?: React.ReactNode;
  layoutVariant?: "dashboard" | "admin";
  authUser?: AuthUser;
  title?: string;
  subtitle?: string;
  icon?: LucideIcon;
}

/**
 * Reusable layout component for individual item edit pages.
 * Provides consistent structure with form content area and right sidebar.
 */
export default function IndividualEditPageLayout({
  children,
  sidebarContent,
  sidebar,
  actions,
  customTopBarContent,
  layoutVariant = "dashboard",
  authUser,
  title,
  subtitle,
  icon: Icon,
}: IndividualEditPageLayoutProps) {
  const [isCompact, setIsCompact] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 1024;
  });

  useEffect(() => {
    const handleResize = () => setIsCompact(window.innerWidth < 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const actualSidebar = sidebarContent || sidebar;

  const pageContent = (
    <div className="h-full bg-background flex flex-col relative">
      <div className="w-full flex-1">
        <div className="flex flex-col lg:flex-row h-full min-h-full">
          {/* Form Content - Left side */}
          <div className={`flex-1 overflow-y-auto ${isCompact && actions ? 'pb-32' : ''}`}>
            {(title || subtitle) && (
              <div className="mb-8 border-b border-slate-100 pb-6 px-8 pt-6">
                <div className="flex items-center gap-3 mb-2">
                  {Icon && (
                    <div className="p-2 bg-blue-50 text-blue-600 rounded-xl">
                      <Icon className="w-5 h-5" />
                    </div>
                  )}
                  {title && <h1 className="text-2xl font-bold text-slate-900">{title}</h1>}
                </div>
                {subtitle && <p className="text-slate-500">{subtitle}</p>}
              </div>
            )}
            <div className={`px-8 pb-6 ${(title || subtitle) ? '' : 'pt-6'}`}>
              {children}
            </div>
          </div>

          {/* Right Sidebar - flush to the edge */}
          {(actualSidebar || (!isCompact && actions)) && (
            <div className={`w-full lg:w-80 border-t lg:border-t-0 lg:border-l border-border flex flex-col bg-slate-50 overflow-y-auto ${isCompact ? 'hidden' : ''}`}>
              {!isCompact && actions && (
                <div className="p-6 border-b border-border bg-white sticky top-0 z-10">
                  {actions}
                </div>
              )}
              {actualSidebar}
            </div>
          )}
        </div>
      </div>

      {/* Mobile Bottom Bar for Actions */}
      {isCompact && actions && (
        <div className="fixed bottom-16 left-0 right-0 p-4 bg-white/80 backdrop-blur-md border-t border-border z-40 shadow-[0_-4px_12px_rgba(0,0,0,0.05)]">
          <div className="max-w-md mx-auto">
            {actions}
          </div>
        </div>
      )}
    </div>
  );

  if (layoutVariant === "admin" && authUser) {
    return (
      <DefaultAdminLayout user={authUser}>{pageContent}</DefaultAdminLayout>
    );
  }

  return (
    <DashboardLayout 
      customTopBarContent={customTopBarContent}
      forceSidebarCollapse={isCompact}
    >
      {pageContent}
    </DashboardLayout>
  );
}
